import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import { useCreateEntityValue, useUpdateEntityValue } from "@/query/entityValue";
import { editingEntityValueAtom } from "@/atom/editing";
import { useAgentId } from "@/hook";
import { errorToast, successToast } from "@/component/Toast";
import { MultiTextInputGroup, MultiTextInputGroupRef, TextInputGroup } from "@/component/input";
import Button from "@/component/Button";


const EntityValueForm: React.FC<{entityId: string}> = ({ entityId }) => {
    const agentId = useAgentId();
    const intl = useIntl();

    const synonymsInputRef = useRef<MultiTextInputGroupRef>(null);
    const [name, setName] = useState<string>("");
    const [mappingValue, setMappingValue] = useState<string>("");
    const [synonyms, setSynonyms] = useState<string[]>([]);
    const [validation, setValidation] = useState<{
        name: boolean,
        synonyms: boolean
    }>({
        name: true, 
        synonyms: true
    });

    const [editingEntityValue, setEditingEntityValue] = useAtom(editingEntityValueAtom);
    const { mutateAsync: createEntityValue, isPending: isCreating } = useCreateEntityValue(agentId, entityId);
    const { mutateAsync: updateEntityValue, isPending: isUpdating } = useUpdateEntityValue(agentId, entityId, editingEntityValue?.id);

    const validate = () => {
        const currentValidation = {
            name: !!name.trim(),
            synonyms: synonyms.length > 0
        };
        setValidation(currentValidation);
        return Object.values(currentValidation).every(value => value);
    };
    const onSubmit = async () => {
        if (!validate()) {
            return;
        }
        const form = {
            name: name.trim(),
            mapping_value: mappingValue?.trim() ?? "",
            synonyms: synonyms
        };
        if (editingEntityValue) {
            await updateEntityValue(form)
                .then(() => {
                    successToast(intl.formatMessage({ id: "t003" }));
                    clear();
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: "t004" }));
                });
        } else {
            await createEntityValue(form)
                .then(() => {
                    successToast(intl.formatMessage({ id: "t001" }));
                    clear();
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: "t002" }));
                });
        }
    };

    const clear = useCallback(() => {
        setEditingEntityValue(undefined);
        synonymsInputRef.current?.clear();
        setName("");
        setMappingValue("");
        setSynonyms([]);
        setValidation({
            name: true,
            synonyms: true
        });
    }, [setEditingEntityValue, setName, setMappingValue, setSynonyms, synonymsInputRef]);

    useEffect(() => {
        if (editingEntityValue) {
            setName(editingEntityValue.name);
            setMappingValue(editingEntityValue.mapping_value);
            setSynonyms(editingEntityValue.synonyms);
        } else {
            clear();
        }
    }, [editingEntityValue, clear]);

    useEffect(() => {
        clear();
    }, [entityId, clear]);

    return (
        <div role="form" className="form-container w-left-editor-section flex-none">
            <div className="form-container-input-group">
                <TextInputGroup rowGap={8}
                                value={name}
                                label={intl.formatMessage({ id: "i05027" })}
                                placeholder={intl.formatMessage({ id: "i05028" })}
                                invalid={!validation.name && !name.trim()}
                                invalidText={intl.formatMessage({ id: "i05046" })}
                                onChange={setName}
                />

                <MultiTextInputGroup ref={synonymsInputRef}
                                     value={synonyms}
                                     label={intl.formatMessage({ id: "i05031" })}
                                     placeholder={intl.formatMessage({ id: "i05032" })}
                                     invalid={!validation.synonyms && !synonyms.length}
                                     invalidText={intl.formatMessage({ id: "i05047" })}
                                     onChange={setSynonyms}
                />

                <TextInputGroup rowGap={8}
                                value={mappingValue}
                                label={intl.formatMessage({ id: "i05029" })}
                                placeholder={intl.formatMessage({ id: "i05030" })}
                                onChange={setMappingValue}
                />
            </div>

            <div className="flex gap-2">
                {editingEntityValue && (
                    <Button variant="secondary"
                            size="full"
                            disabled={isUpdating || isCreating}
                            onClick={clear}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                )}
                <Button variant="primary"
                        size="full"
                        disabled={isUpdating || isCreating}
                        onClick={onSubmit}
                >
                    {intl.formatMessage({ id: editingEntityValue ? "b0001" : "b0010" })}
                </Button>
            </div>
        </div>
    );
};
export default EntityValueForm;
